import React from 'react';
import { Card } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';

interface MatrixHeatmapProps {
    n: number;
    missingPairs: number[][];
}

const MatrixHeatmap: React.FC<MatrixHeatmapProps> = ({ n, missingPairs }) => {
    // 构建缺失集合用于快速查找
    const missingSet = new Set(
        missingPairs.map(([row, col]) => `${row},${col}`)
    );

    // 生成数字标签（从1开始）
    const getLabel = (index: number): string => {
        return String(index + 1); // 1, 2, 3, ...
    };

    // 检查是否缺失
    const isMissing = (row: number, col: number): boolean => {
        return row !== col && missingSet.has(`${row},${col}`);
    };

    const cellSize = n <= 10 ? 40 : n <= 15 ? 32 : 24;
    const fontSize = n <= 10 ? '16px' : n <= 15 ? '14px' : '12px';

    return (
        <Card title="📊 缓存命中可视化">
            <div className="overflow-auto">
                <div className="inline-block min-w-full">
                    <div className="flex">
                        {/* 左上角空白 */}
                        <div
                            style={{
                                width: cellSize,
                                height: cellSize,
                                minWidth: cellSize,
                                minHeight: cellSize
                            }}
                        />
                        {/* 列标签 */}
                        {Array.from({ length: n }).map((_, col) => (
                            <div
                                key={`col-label-${col}`}
                                className="flex items-center justify-center font-bold text-gray-700"
                                style={{
                                    width: cellSize,
                                    height: cellSize,
                                    minWidth: cellSize,
                                    minHeight: cellSize,
                                    fontSize
                                }}
                            >
                                {getLabel(col)}
                            </div>
                        ))}
                    </div>

                    {/* 矩阵内容 */}
                    {Array.from({ length: n }).map((_, row) => (
                        <div key={`row-${row}`} className="flex">
                            {/* 行标签 */}
                            <div
                                className="flex items-center justify-center font-bold text-gray-700"
                                style={{
                                    width: cellSize,
                                    height: cellSize,
                                    minWidth: cellSize,
                                    minHeight: cellSize,
                                    fontSize
                                }}
                            >
                                {getLabel(row)}
                            </div>

                            {/* 单元格 */}
                            {Array.from({ length: n }).map((_, col) => {
                                const isDiagonal = row === col;
                                const missing = isMissing(row, col);

                                return (
                                    <div
                                        key={`cell-${row}-${col}`}
                                        className="flex items-center justify-center border border-gray-200"
                                        style={{
                                            width: cellSize,
                                            height: cellSize,
                                            minWidth: cellSize,
                                            minHeight: cellSize,
                                            backgroundColor: isDiagonal
                                                ? '#f0f0f0'
                                                : missing
                                                ? '#fff1f0'
                                                : '#f6ffed',
                                            fontSize
                                        }}
                                        title={
                                            isDiagonal
                                                ? '对角线'
                                                : missing
                                                ? `${getLabel(row)}→${getLabel(col)} 未缓存`
                                                : `${getLabel(row)}→${getLabel(col)} 已缓存`
                                        }
                                    >
                                        {isDiagonal ? (
                                            <span className="text-gray-400">-</span>
                                        ) : missing ? (
                                            <CloseOutlined style={{ color: '#ff4d4f' }} />
                                        ) : (
                                            <CheckOutlined style={{ color: '#52c41a' }} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* 图例 */}
                <div className="flex gap-6 justify-center mt-4 text-sm">
                    <div className="flex items-center gap-2">
                        <CheckOutlined style={{ color: '#52c41a' }} />
                        <span>已缓存 ({n * n - n - missingPairs.length})</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <CloseOutlined style={{ color: '#ff4d4f' }} />
                        <span>未缓存 ({missingPairs.length})</span>
                    </div>
                </div>
            </div>
        </Card>
    );
};

export default MatrixHeatmap;
